import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AxiosPromise } from 'axios';

import { LoadingPage } from 'Shared/components';
import { useFullstory, useRewardsEnrollment } from 'Shared/hooks';

import {
  GetDetailsResponseType,
  PerformCommandType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { DictionaryType } from 'QuoteApp/services/endpoints/GetDictionary';

import {
  useDetails,
  useRate,
  useConfig,
  useAvailableProducts,
  useApi,
  useDictionary,
  QuoteAppApiProviderType,
} from './hooks';
import { GetRewardsEnrollmentResponse } from '@clearcover/core-app-types';
import {
  EstimateRewardPointsProviderType,
  EstimateRewardPointsResponse,
  useEstimateRewardPoints,
} from './hooks/useEstimateRewardPoints/useEstimateRewardPoints';
import { PaymentPlanType } from '../../endpoints/GetPaymentDetails';
import { useSurvicate } from 'Shared/hooks/useSurvicate/useSurvicate';
import { GetAvailableProductsResponseType } from 'QuoteApp/services/endpoints/GetAvailableProducts';
import { useInitializeCoveragesCommand } from './hooks/useInitializeCoverages';
import { useSetMinimumCoveragesCommand } from './hooks/useSetMinimumCoverages';

export type AppProviderType = {
  appId: string;
  config: GetConfigResponseType | null;
  configLoading: boolean;
  refreshConfig(): Promise<GetConfigResponseType>;
  availableProducts: GetAvailableProductsResponseType | null;
  availableProductsLoading: boolean;
  refreshAvailableProducts(): Promise<GetAvailableProductsResponseType>;
  details: GetDetailsResponseType;
  detailsLoading: boolean;
  getRateOnLoad(force?: boolean): void;
  refreshDetails(): Promise<GetDetailsResponseType>;
  setDetails(newDetails: GetDetailsResponseType): void;
  rate: RateType | null;
  rateLoading: boolean;
  rateError: boolean;
  fetchRate(): Promise<RateType | null>;
  refreshRate(): void;

  dictionary: DictionaryType | null;
  dictionaryLoading: boolean;
  dictionaryError: boolean;
  fetchDictionary(): Promise<DictionaryType | null>;

  isUnauthorized: boolean;
  canNavigate: boolean;
  changeCanNavigate(value: boolean): void;
  api: QuoteAppApiProviderType['api'];
  displayFCRANotice: boolean;
  changeDisplayFCRANotice(value: boolean): void;
  rewardsEnrollmentResponse: GetRewardsEnrollmentResponse | null;
  rewardsEnrollmentLoading: boolean;
  isEstimatePointsLoading: boolean;
  rewardsPointsError: boolean | null;
  submitEstimatePoints: EstimateRewardPointsProviderType['submitEstimatePoints'];
  estimatePointsResponse: EstimateRewardPointsResponse | null;
  selectedPaymentPlan: PaymentPlanType | null | undefined;
  changeSelectedPaymentPlan(value: PaymentPlanType | null | undefined): void;
  acknowledgeQuoteChange(): void;
  initializeCoverages: PerformCommandType | null;
  setMinimumCoverages: PerformCommandType | null;
};

export const AppProviderContext = createContext<AppProviderType>({
  appId: '',
  config: null,
  configLoading: false,
  availableProducts: null,
  availableProductsLoading: false,
  refreshAvailableProducts: () =>
    Promise.resolve([] as GetAvailableProductsResponseType),
  details: {} as GetDetailsResponseType,
  detailsLoading: false,
  getRateOnLoad: () => {
    /* do nothing */
  },
  rate: null,
  rateLoading: false,
  rateError: false,
  fetchRate: () => Promise.resolve(null),

  dictionary: null,
  dictionaryLoading: false,
  dictionaryError: false,
  fetchDictionary: () => Promise.resolve(null),

  refreshConfig: () => Promise.resolve({} as GetConfigResponseType),
  refreshDetails: () => Promise.resolve({} as GetDetailsResponseType),
  refreshRate: () => {
    /* do nothing */
  },
  setDetails: (_newDetails: GetDetailsResponseType) => {
    /* do nothing */
  },
  isUnauthorized: false,
  canNavigate: true,
  changeCanNavigate: () => {
    /* do nothing */
  },
  api: {
    loading: false,
    saveAppData: () => Promise.resolve({}) as AxiosPromise,
    saveCoverages: () => Promise.resolve({}) as AxiosPromise,
  },
  displayFCRANotice: false,
  changeDisplayFCRANotice: () => {
    /* do nothing */
  },
  rewardsEnrollmentResponse: null,
  rewardsEnrollmentLoading: false,
  isEstimatePointsLoading: false,
  rewardsPointsError: null,
  submitEstimatePoints: () => Promise.resolve(null),
  estimatePointsResponse: null,
  selectedPaymentPlan: null,
  changeSelectedPaymentPlan: () => {
    /* do nothing */
  },
  acknowledgeQuoteChange: () => {
    /* do nothing */
  },
  initializeCoverages: null,
  setMinimumCoverages: null,
});

export const useAppContext = () => useContext(AppProviderContext);
export const useAppAvailableProducts = () => {
  const {
    availableProducts,
    availableProductsLoading,
    refreshAvailableProducts,
  } = useAppContext();

  useEffect(() => {
    if (!availableProducts && !availableProductsLoading) {
      refreshAvailableProducts();
    }
  }, [availableProducts, availableProductsLoading, refreshAvailableProducts]);

  return availableProducts;
};

export const AppProvider: FC<{
  appId: string;
  path: string;
  children: (ctx: AppProviderType) => ReactNode;
}> = ({ appId, path, children }) => {
  useSurvicate();
  const [canNavigate, changeCanNavigate] = useState<boolean>(true);
  const [displayFCRANotice, changeDisplayFCRANotice] = useState<boolean>(false);
  const [selectedPaymentPlan, changeSelectedPaymentPlan] = useState<
    PaymentPlanType | null | undefined
  >(null);
  const { config, configLoading, refreshConfig } = useConfig(appId);
  const {
    availableProducts,
    availableProductsLoading,
    refreshAvailableProducts,
  } = useAvailableProducts(appId);
  const { isUnauthorized, handleUnauthorizedAgent, api } = useApi(appId);
  const {
    rewardsEnrollmentLoading,
    rewardsEnrollmentResponse,
  } = useRewardsEnrollment();
  const {
    isEstimatePointsLoading,
    submitEstimatePoints,
    estimatePointsResponse,
    rewardsPointsError,
  } = useEstimateRewardPoints();

  const {
    details,
    setDetails,
    refreshDetails,
    loading: detailsLoading,
    acknowledgeQuoteChange,
  } = useDetails(appId, isUnauthorized, handleUnauthorizedAgent);

  const initializeCoverages = useInitializeCoveragesCommand(
    details,
    setDetails
  );

  const setMinimumCoverages = useSetMinimumCoveragesCommand(
    details,
    setDetails
  );

  const { fsCommands } = useFullstory();

  const {
    rate,
    error: rateError,
    fetchRate,
    refreshRate,
    loading: rateLoading,
  } = useRate(appId, refreshDetails);

  const {
    dictionaryLoading,
    dictionaryError,
    dictionary,
    fetchDictionary,
  } = useDictionary(appId);

  const getRateOnLoad = useCallback(
    (force: boolean = false) => {
      const rateHasLoaded = !!(rate || rateError);
      if (force || (!rateHasLoaded && !rateLoading)) {
        refreshRate();
      }
    },
    [rate, rateError, rateLoading, refreshRate]
  );

  useEffect(() => {
    if (
      !isUnauthorized &&
      path !== 'start' &&
      details &&
      !['out_of_market', 'dead'].includes(details.status) &&
      details.address[details.principalInsuredId].state.value &&
      !config &&
      !configLoading
    ) {
      refreshConfig();
    }
  }, [config, configLoading, details, isUnauthorized, path, refreshConfig]);

  useEffect(() => {
    const productSegment = details?.productSegment;
    const addressState =
      details?.address?.[details?.principalInsuredId]?.state.value;
    const quoteNumber = details?.quoteNumber;

    fsCommands.updatePersistedFullstoryMetadata({
      new_quote_number: quoteNumber || 'none',
      new_insurance_application_id: appId || 'none',
      product_segment: productSegment || 'none',
      policy_enforced_state: addressState || 'none',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, appId]);

  useEffect(() => {
    if (!!path) {
      fsCommands.setPersistedFullstoryPageProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (
      (path === 'coverages' || path === 'summary' || path === 'underwriting') &&
      !dictionaryLoading &&
      !dictionary
    ) {
      fetchDictionary()
        .then()
        .catch();
    }
  }, [path, dictionaryLoading, dictionary, fetchDictionary]);

  if (!details) {
    return <LoadingPage />;
  }

  return (
    <AppProviderContext.Provider
      value={{
        appId,
        config,
        configLoading,
        refreshConfig,
        availableProducts,
        availableProductsLoading,
        refreshAvailableProducts,
        details,
        detailsLoading,
        refreshDetails,
        setDetails,
        getRateOnLoad,
        rate,
        rateLoading,
        rateError,
        refreshRate,
        fetchRate,
        dictionaryLoading,
        dictionaryError,
        dictionary,
        fetchDictionary,
        isUnauthorized,
        canNavigate,
        changeCanNavigate,
        api,
        displayFCRANotice,
        changeDisplayFCRANotice,
        rewardsEnrollmentLoading,
        rewardsEnrollmentResponse,
        isEstimatePointsLoading,
        rewardsPointsError,
        submitEstimatePoints,
        estimatePointsResponse,
        selectedPaymentPlan,
        changeSelectedPaymentPlan,
        acknowledgeQuoteChange,
        initializeCoverages,
        setMinimumCoverages,
      }}
    >
      {children({
        appId,
        config,
        configLoading,
        refreshConfig,
        availableProducts,
        availableProductsLoading,
        refreshAvailableProducts,
        details,
        detailsLoading,
        refreshDetails,
        setDetails,
        getRateOnLoad,
        rate,
        rateLoading,
        rateError,
        refreshRate,
        fetchRate,
        dictionaryLoading,
        dictionaryError,
        dictionary,
        fetchDictionary,
        isUnauthorized,
        canNavigate,
        changeCanNavigate,
        api,
        displayFCRANotice,
        changeDisplayFCRANotice,
        rewardsEnrollmentLoading,
        rewardsEnrollmentResponse,
        isEstimatePointsLoading,
        rewardsPointsError,
        submitEstimatePoints,
        estimatePointsResponse,
        selectedPaymentPlan,
        changeSelectedPaymentPlan,
        acknowledgeQuoteChange,
        initializeCoverages,
        setMinimumCoverages,
      })}
    </AppProviderContext.Provider>
  );
};
